import axios from 'axios';

import { agentEndpoints } from './endpoints/agents';
import { aiEndpoints } from './endpoints/ai';
import { analyticsEndpoints } from './endpoints/analytics';
import { authEndpoints } from './endpoints/auth';
import { b2bEndpoints } from './endpoints/b2b';
import { blogsEndpoints } from './endpoints/blogs';
import { cartEndpoints } from './endpoints/cart';
import { catalogEndpoints } from './endpoints/catalog';
import { categoryEndpoints } from './endpoints/categories';
import { cmsEndpoints } from './endpoints/cms';
import { commissionsEndpoints } from './endpoints/commissions';
import { courierFinanceEndpoints } from './endpoints/courierFinance';
import { crmEndpoints } from './endpoints/crm';
import { customerEndpoints } from './endpoints/customers';
import { erpEndpoints } from './endpoints/erp';
import { fileEndpoints } from './endpoints/files';
import { globalizationEndpoints } from './endpoints/globalization';
import { importEndpoints } from './endpoints/import';
import { intelligenceEndpoints } from './endpoints/intelligence';
import { marketingEndpoints } from './endpoints/marketing';
import { mobileEndpoints } from './endpoints/mobile';
import { networkEndpoints } from './endpoints/network';
import { noteEndpoints } from './endpoints/notes';
import { notificationsEndpoints } from './endpoints/notifications';
import { orderEndpoints } from './endpoints/orders';
import { paymentEndpoints } from './endpoints/payments';
import { platformEndpoints } from './endpoints/platform';
import { posEndpoints } from './endpoints/pos';
import { productEndpoints } from './endpoints/products';
import { reportEndpoints } from './endpoints/reports';
import { reviewEndpoints } from './endpoints/reviews';
import { rolesEndpoints } from './endpoints/roles';
import { saasEndpoints } from './endpoints/saas';
import { scaleEndpoints } from './endpoints/scale';
import { settingsEndpoints } from './endpoints/settings';
import { settlementsEndpoints } from './endpoints/settlements';
import { sponsoredEndpoints } from './endpoints/sponsored';
import { supportEndpoints } from './endpoints/support';
import { userEndpoints } from './endpoints/users';
import { vendorEndpoints } from './endpoints/vendors';
import { walletEndpoints } from './endpoints/wallet';
import { createHttp, type HttpConfig } from './http';
import { createMemoryTokenStore, type TokenStore } from './token-store';

export interface CreateClientOptions {
  baseURL: string;
  /** Defaults to an in-memory token store. */
  tokenStore?: TokenStore;
  onAuthFailure?: () => void;
  timeoutMs?: number;
  withCredentials?: boolean;
}

/**
 * Build a fully wired API client. The refresh flow is bound to this client's own `/auth/refresh`
 * (using a bare axios instance to avoid the 401-retry interceptor recursing).
 */
export function createApiClient(opts: CreateClientOptions) {
  const tokenStore = opts.tokenStore ?? createMemoryTokenStore();

  const refreshAxios = axios.create({
    baseURL: opts.baseURL,
    withCredentials: opts.withCredentials ?? true,
    timeout: opts.timeoutMs ?? 20_000,
  });

  /**
   * Trade an expired access token for a fresh one.
   *
   * In a browser the refresh token is an httpOnly cookie, so the empty body
   * below is enough — the browser attaches it. A native app has no cookie jar
   * and keeps the token in the OS keystore instead, so if the store can produce
   * one it is sent in the body, which is exactly why the endpoint accepts it
   * there. Without this the mobile app had nothing to refresh with: the first
   * 401 after the access token aged out ended the session.
   */
  const refreshAccessToken: HttpConfig['refreshAccessToken'] = async () => {
    const stored = (await tokenStore.getRefreshToken?.()) ?? null;
    const { data } = await refreshAxios.post<{
      tokens: { accessToken: string; refreshToken?: string };
    }>('/auth/refresh', stored ? { refreshToken: stored } : {});
    // Refresh tokens rotate. A client holding its own has to write the new one
    // back, or the next refresh presents one the server has already retired.
    if (data?.tokens?.refreshToken && tokenStore.setRefreshToken) {
      await tokenStore.setRefreshToken(data.tokens.refreshToken);
    }
    return data?.tokens?.accessToken ?? null;
  };

  const httpCfg: HttpConfig = {
    baseURL: opts.baseURL,
    tokenStore,
    refreshAccessToken,
  };
  if (opts.onAuthFailure !== undefined) httpCfg.onAuthFailure = opts.onAuthFailure;
  if (opts.timeoutMs !== undefined) httpCfg.timeoutMs = opts.timeoutMs;
  if (opts.withCredentials !== undefined) httpCfg.withCredentials = opts.withCredentials;
  const http = createHttp(httpCfg);

  return {
    /** Underlying axios instance — escape hatch for endpoints not yet wrapped. */
    http,
    tokenStore,
    setAccessToken: (token: string | null) => tokenStore.setAccessToken(token),
    auth: authEndpoints(http),
    users: userEndpoints(http),
    vendors: vendorEndpoints(http),
    products: productEndpoints(http),
    categories: categoryEndpoints(http),
    catalog: catalogEndpoints(http),
    productImport: importEndpoints(http),
    cms: cmsEndpoints(http),
    commissions: commissionsEndpoints(http),
    customers: customerEndpoints(http),
    agents: agentEndpoints(http),
    files: fileEndpoints(http),
    reports: reportEndpoints(http),
    notes: noteEndpoints(http),
    cart: cartEndpoints(http),
    orders: orderEndpoints(http),
    payments: paymentEndpoints(http),
    wallet: walletEndpoints(http),
    mobile: mobileEndpoints(http),
    settlements: settlementsEndpoints(http),
    courierFinance: courierFinanceEndpoints(http),
    crm: crmEndpoints(http),
    notifications: notificationsEndpoints(http),
    support: supportEndpoints(http),
    erp: erpEndpoints(http),
    pos: posEndpoints(http),
    b2b: b2bEndpoints(http),
    reviews: reviewEndpoints(http),
    saas: saasEndpoints(http),
    analytics: analyticsEndpoints(http),
    intelligence: intelligenceEndpoints(http),
    roles: rolesEndpoints(http),
    ai: aiEndpoints(http),
    network: networkEndpoints(http),
    platform: platformEndpoints(http),
    scale: scaleEndpoints(http),
    globalization: globalizationEndpoints(http),
    blogs: blogsEndpoints(http),
    marketing: marketingEndpoints(http),
    settings: settingsEndpoints(http),
    sponsored: sponsoredEndpoints(http),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
